import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { useEffect, useState } from 'react';
import { Box, Grid, ToggleButton, ToggleButtonGroup } from '@mui/material';

import '../../styles/historyBarGraph.scss';

import { getCookie } from '../../data/Cookies';

import useChargingHistory from '../../hooks/useChargingHistory';
import formatOrdinal from '../../hooks/ordinalFormater';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export enum GraphType {
    energy = "Energy",
    emissions = "Emissions"
}

export enum Period {
    week = "week",
    month = "month",
    year = "year"
}

export default function HistoryBarGraph({ passPeriod }) {

    const [period, setPeriod] = useState<Period>(Period.week);
    
    const [type, setType] = useState<GraphType>(GraphType.energy);

    const [options, setOptions] = useState({
        events: [], // disable pop-up displayed when hovering
        animation: {
            duration: 0 // disable animation when reloading data
        },
        plugins: {
            legend: {
                display: false // hide dataset labels
            }
        },
        scales: {
            x: {
                grid: {
                    display: false, // don't display vertical guide lines
                },
                ticks: {
                    //autoSkip: true, // force display all labels
                }
            },
            y: {
                ticks: {
                    // format axis labels
                    callback: function(value, index, ticks) {
                        return value + " kWh";
                    },
            }
          }
        }
    });

    const [graphData, setGraphData] = useState(
        {
            labels: [],
            datasets: [
                {
                    borderRadius: 10,
                    id: 1,
                    data: [],
                    backgroundColor: '#72C563',
                }
            ],
        });

    function selectPeriod(period : Period) {
        if (period == null) {
            return;
        } else {
            setPeriod(period);
        }
    }

    const {history, getHistory} = useChargingHistory();

    function adjustDisplayFromToday(list, period : Period, label : boolean) {
        const now = new Date(Date.now());
        var cutoff = 0; 
        var length; 

        switch (period) {
            case Period.week:
                cutoff = now.getDay();
                length = 7;
                if (label) cutoff -= 1;
                break;
            case Period.month:
                cutoff = now.getDate();
                length = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
                break;
            case Period.year:
                cutoff = now.getMonth();
                length = 12;
                cutoff += 1;
                break;
        }

        list = (list.slice(cutoff, length)).concat(list.slice(0, cutoff));
        list.reverse();
        return list;
    }

    var weekLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    var monthLabels = Array.from(Array(32).keys());
    var yearLabels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    function createLabels() {
        const now = new Date(Date.now());

        weekLabels = adjustDisplayFromToday(weekLabels, Period.week, true)
        monthLabels = adjustDisplayFromToday(monthLabels, Period.month, true);

        var i = 0;
        monthLabels.forEach((day) => {
            monthLabels[i] = formatOrdinal(day);
            i ++;
        })

        yearLabels = adjustDisplayFromToday(yearLabels, Period.year, true);
    }

    useEffect(() => passPeriod(period), [period]);

    useEffect(updateGraphData, [type, period]);

    useEffect(() => {
        getHistory();
        updateGraphData();
    }, [JSON.stringify(history)])

    function updateGraphData() {
        if (history === undefined) return;

        createLabels();

        var newLabels;
        var newColor;
        var newDataset;
        var newAxisFormatting;

        switch (period) {
            case Period.week:
                newLabels = weekLabels;
                newDataset = history["week"];
                break;
            case Period.month:
                newLabels = monthLabels;
                newDataset = history["month"];
                break;
            case Period.year:
                newLabels = yearLabels;
                newDataset = history["year"];
                break;
        }

        switch (type) {
            case GraphType.energy:
                newDataset = newDataset.map(function (entry) { return entry.energy; });
                newColor = '#72C563';
                newAxisFormatting = function(value, index, ticks) { return value + " kWh"; };
                break;
            case GraphType.emissions:
                newDataset = newDataset.map(function (entry) { return entry.emissions; });
                newColor = '#454545'
                newAxisFormatting = function(value, index, ticks) { return value; };
                break;
        }

        setGraphData({
            labels: newLabels,
            datasets: [
                {
                    borderRadius: 10,
                    id: 1,
                    data: newDataset,
                    backgroundColor: newColor,
                }
            ],
        })

        options.scales.y.ticks.callback = newAxisFormatting;
        setOptions(options);
    }

    return(
        <Box className = "boxBorder">

                <Grid
                container
                direction = "row"
                justifyContent = "center">

                    <Grid item xs = {12}>
                        <ToggleButtonGroup
                            value = {type}
                            exclusive = {true}
                            className = "graphTypeSelector"
                            onChange={(e, selected) => setType(selected)}
                            >
                            <ToggleButton value = {GraphType.energy}>Energy</ToggleButton>
                            <ToggleButton value = {GraphType.emissions}>CO2 Emissions</ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>

                    <Grid item xs={12}>
                        <Bar className = "graph" options={options} data={graphData} redraw={true}/>
                    </Grid>

                    <Grid item alignItems = "stretch" justifyContent="center">
                        <ToggleButtonGroup
                            value={period} 
                            exclusive
                            color='success'
                            className = "periodSelector"
                            onChange={(e, selected) => selectPeriod(selected)}
                            >
                            <ToggleButton value = {Period.week}>Week</ToggleButton>
                            <ToggleButton value = {Period.month}>Month</ToggleButton>
                            <ToggleButton value = {Period.year}>Year</ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>

                </Grid>
            </Box>
    );
}